import ApiClient from '../backend/ApiClient'
import { Route } from '../components/routing/Routing'
import { snackbar } from '../components/services/SnackbarService'
import affirm from '../core/Affirm'
import algo from '../core/Algo'
import { IUserRole } from '../definitions/Permissions'
import { IUser } from '../definitions/Users'
import Helper from '../helpers/Helper'
import { IRolesEntitlements, Permission } from '../os_definitions/OsPermissions'
import { store } from '../store/Store'

export const RoutePermissions: Record<Route, Permission> = {
    'overview': 'overview.view',
    'playground': 'playground.view',
    'contracts': 'contracts.view',
    'contract-details': 'contract-details.view',
    'provider': 'provider.view',
    'provider-group': 'provider-group.view',
    'funds': 'funds.view',
    'fund': 'fund.view',
    'claims': 'claims.view',
    'quality-measures-breakdown': 'quality-measures-breakdown.view',
    'provider-performance-breakdown': 'provider-performance-breakdown.view',
    'funds-breakdown': 'funds-breakdown.view',
    'claims-breakdown': 'claims-breakdown.view',
    'network': 'network.view',
    'members': 'members.view',
    'contracts-manager': 'contracts-manager.view',
    'contract-editor': 'contract-editor.view',
    'tcoc-summary': 'tcoc-summary.view',
    'provider-contracts': 'provider-contracts.view',
    'member-details': 'member-details.view',
    'workflows': 'workflows.view',
    'flows': 'flows.view',
    'flow': 'flow.view',
    'contracts-analysis': 'contracts-analysis.view',
    'compare-contracts-csi': 'compare-contracts-csi.view',
    'settlement-details': 'settlement-details.view',
    'user-profile': 'user-profile.view',
    'sign-in': 'sign-in.view',
    'administrator': 'administrator.view',
    'user-editor': 'user-editor.view',
    'tooltip-admin': 'tooltip-admin.view',
    'contract-schema': 'contract-schema.view',
    'contract-schema-editor': 'contract-schema.view',
    'user-management': 'user-management.view',
    'report-editor': 'report-editor.view',
    'media': 'member-details.view',
    'approvals': 'approvals.view',
    'users-approvals': 'users-approvals.view',
    'groups-management': 'groups-management.view',
    'groups-editor': 'groups-editor.view',
    'approval-audit': 'approval-audit.view',
    'multirun-result': 'multirun-result.view',
    'contract-schema-manager': 'contract-schema.edit'
}

class PermissionManagerClass {
    hasPermission = (feature: string, currentUser: IUser): boolean => {
        affirm(currentUser, 'User not found')
        affirm(feature, 'The feature can\'t be empty')
        affirm(!feature.includes('*'), 'The feature can\'t include *')

        const userRoles = currentUser.roles ?? []
        const { allow: rolesAllow, disallow: rolesDisallow } = this.getRolePermissions(userRoles)
        const updatedAllows = this.addViewPermissions(rolesAllow, rolesDisallow)
        const res = this.isAllowed(feature, updatedAllows, rolesDisallow)

        return res
    }

    isAllowed = (feature: string, allowed: string[], disallowed: string[]) => {
        const matchesRule = (rule: string, featureToCheck: string): boolean => {
            const ruleParts = rule.toLocaleLowerCase().split('.')
            const featureParts = featureToCheck.toLocaleLowerCase().split('.')
            const minLength = ruleParts.length < featureParts.length ? ruleParts.length : featureParts.length
            for (let i = 0; i < minLength; i++) {
                if (ruleParts[i] !== featureParts[i] && ruleParts[i] !== '*') return false
            }
            return true
        }

        const isMatch = (allowedList: string[], feature: string): boolean => {
            let res: boolean = false
            res = allowedList.some(rule => {
                if (rule === '*') return true
                if (rule === feature) return true
                if (matchesRule(rule, feature)) return true
            })
            return res
        }
        if (isMatch(disallowed, feature)) return false
        if (isMatch(allowed, feature)) return true
        return false
    }

    getRolePermissions = (roles: string[]): IRolesEntitlements => {
        let rolesAllow: string[] = []
        let rolesDisallow: string[] = []

        const allRolesArray: IUserRole[] = store.getState().session.allUserRoles

        const allRoles: Record<string, IRolesEntitlements> = allRolesArray.reduce((acc, role) => {
            acc[role.name] = {
                allow: role.entitlements.allow ?? [],
                disallow: role.entitlements.disallow ?? []
            }
            return acc
        }, {} as Record<string, IRolesEntitlements>)

        if (roles) {
            roles.map(role => {
                const roleConfig = allRoles[role]
                if (roleConfig) {
                    rolesAllow = rolesAllow.concat(roleConfig.allow)
                    rolesDisallow = rolesDisallow.concat(roleConfig.disallow)
                }
            })
        }
        return { allow: rolesAllow, disallow: rolesDisallow }
    }

    addViewPermissions = (allow: string[], disallow: string[]): string[] => {
        const newAllowPermissions = allow.slice()

        allow.forEach(permission => {
            if (permission) {
                const resourceParts = permission.split('.').slice(0, -1)
                const action = permission.split('.').pop()

                if (Helper.hasVal(action) && action !== 'view' && action !== '*') {
                    const resource = resourceParts.join('.')
                    const viewPermission = `${resource}.view`

                    if (!disallow.includes(viewPermission) &&
                        !newAllowPermissions.includes(viewPermission) &&
                        !newAllowPermissions.includes(`${resource}.*`)) {
                        newAllowPermissions.push(viewPermission)
                    }
                }
            }
        })
        return newAllowPermissions
    }

    createGroup = async (name: string, description: string, isDefault: boolean, allow: string[], disallow: string[]): Promise<IUserRole[]> => {
        affirm.hasValue(name, 'Invalid Name')
        affirm.hasValue(description, 'Invalid Description')
        affirm.hasValue(isDefault, 'IsDefault should be true or false')
        affirm.hasValue(allow, 'Invalid Allow Entitlements')
        affirm.hasValue(disallow, 'Invalid Disallow Entitlements')

        const allRoles: IUserRole[] = store.getState().session.allUserRoles
        const roleExists: boolean = allRoles.some(role => role.name === name)
        if (roleExists) {
            snackbar.show({ severity: 'error', text: `Role: ${name} already exists` })
            throw new Error(`Role: ${name} already exists`)
        }

        try {
            const response = await ApiClient.createGroup(name, description, isDefault, allow, disallow)
            if (response) {
                snackbar.show({ severity: 'success', text: `Role was created successfully` })
                return response
            }
        } catch (error) {
            console.error('Error creating Role:', error)
            snackbar.show({ severity: 'error', text: `Unable to create UserRole` })
        }
    }

    updateGroup = async (uuid: string, name: string, description: string, isDefault: boolean, allow: string[], disallow: string[]): Promise<IUserRole[]> => {
        const allRoles = store.getState().session.allUserRoles

        try {
            const existingRole = allRoles.find(role => role.uuid === uuid)

            if (!algo.hasVal(existingRole)) {
                throw new Error('Role not found')
            }
            const updatedGroup: IUserRole = {
                ...existingRole,
                name,
                description,
                isDefault,
                entitlements: { allow, disallow }
            }
            const response = await ApiClient.updateGroup(updatedGroup)
            if (response) {
                snackbar.show({ severity: 'success', text: `UserRole ${updatedGroup.name} updated successfully` })
                return response
            }
        } catch (error) {
            console.error('Error updating Role: ', error)
            snackbar.show({ severity: 'error', text: `Unable to update UserRole: ${uuid}` })
        }
    }

    deleteGroup = async (uuid: string): Promise<IUserRole[]> => {
        affirm.hasValue(uuid, 'Invalid User Role UUID')

        try {
            const response = await ApiClient.deleteGroup(uuid)
            if (response) {
                snackbar.show({ severity: 'success', text: `Role deleted successfully` })
                return response
            }
        } catch (error) {
            console.error('Error deleting Role:', error)
            snackbar.show({ severity: 'error', text: `Unable to delete UserRole : ${uuid}` })
            throw error
        }
    }
}

const PermissionManager = new PermissionManagerClass()
export default PermissionManager
